import axios from 'axios';

// 创建axios实例
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 添加请求时间戳（用于缓存控制）
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }

    // 开发环境下打印请求信息
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
        params: config.params
      });
    }

    return config;
  },
  (error) => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 开发环境下打印响应信息
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 开发环境下打印错误信息
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        message: error.response?.data?.message || error.message
      });
    }

    // 处理401错误（token过期）
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/refresh`,
            { refreshToken }
          );

          if (response.data.status === 'success') {
            const { accessToken, refreshToken: newRefreshToken } = response.data.data;
            
            localStorage.setItem('token', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            
            // 重新发送原始请求
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('刷新token失败:', refreshError);
        // 刷新失败，清除本地存储并跳转到登录页
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      }
    }

    // 处理网络错误
    if (!error.response) {
      error.message = '网络连接失败，请检查网络设置';
    } else {
      // 根据状态码设置错误消息
      switch (error.response.status) {
        case 400:
          error.message = error.response.data?.message || '请求参数错误';
          break;
        case 401:
          error.message = '认证失败，请重新登录';
          break;
        case 403:
          error.message = '权限不足，无法访问该资源';
          break;
        case 404:
          error.message = '请求的资源不存在';
          break;
        case 429:
          error.message = '请求过于频繁，请稍后再试';
          break;
        case 500:
          error.message = '服务器内部错误，请稍后重试';
          break;
        case 502:
          error.message = '网关错误，请稍后重试';
          break;
        case 503:
          error.message = '服务暂时不可用，请稍后重试';
          break;
        default:
          error.message = error.response.data?.message || '请求失败，请稍后重试';
      }
    }

    return Promise.reject(error);
  }
);

// API方法封装
const apiMethods = {
  // GET请求
  get: (url, params = {}, config = {}) => {
    return api.get(url, { params, ...config });
  },

  // POST请求
  post: (url, data = {}, config = {}) => {
    return api.post(url, data, config);
  },

  // PUT请求
  put: (url, data = {}, config = {}) => {
    return api.put(url, data, config);
  },

  // PATCH请求
  patch: (url, data = {}, config = {}) => {
    return api.patch(url, data, config);
  },

  // DELETE请求
  delete: (url, config = {}) => {
    return api.delete(url, config);
  },

  // 文件上传
  upload: (url, formData, config = {}) => {
    return api.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers
      }
    });
  },

  // 下载文件
  download: (url, params = {}, filename = '') => {
    return api.get(url, {
      params,
      responseType: 'blob'
    }).then(response => {
      // 创建下载链接
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // 从响应头获取文件名或使用提供的文件名
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition && !filename) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/); 
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return response;
    });
  }
};

// 特定API端点的封装
export const authAPI = {
  login: (credentials) => apiMethods.post('/auth/login', credentials),
  logout: (refreshToken) => apiMethods.post('/auth/logout', { refreshToken }),
  register: (userData) => apiMethods.post('/auth/register', userData),
  refreshToken: (refreshToken) => apiMethods.post('/auth/refresh', { refreshToken }),
  getProfile: () => apiMethods.get('/auth/me'),
  updateProfile: (data) => apiMethods.put('/auth/profile', data),
  changePassword: (data) => apiMethods.put('/auth/password', data)
};

export const newsAPI = {
  getList: (params) => apiMethods.get('/news', params),
  getById: (id) => apiMethods.get(`/news/${id}`),
  create: (data) => apiMethods.post('/news', data),
  update: (id, data) => apiMethods.put(`/news/${id}`, data),
  delete: (id) => apiMethods.delete(`/news/${id}`),
  like: (id) => apiMethods.post(`/news/${id}/like`),
  share: (id) => apiMethods.post(`/news/${id}/share`),
  uploadImage: (formData) => apiMethods.upload('/news/upload', formData)
};

export const contactAPI = {
  submit: (data) => apiMethods.post('/contact', data),
  create: (data) => apiMethods.post('/contact', data),
  getList: (params) => apiMethods.get('/contact', params),
  getById: (id) => apiMethods.get(`/contact/${id}`),
  update: (id, data) => apiMethods.put(`/contact/${id}`, data),
  delete: (id) => apiMethods.delete(`/contact/${id}`),
  addNote: (id, note) => apiMethods.post(`/contact/${id}/notes`, { note }),
  addFollowUp: (id, data) => apiMethods.post(`/contact/${id}/followups`, data),
  getStats: (params) => apiMethods.get('/contact/stats', params),
  export: (params) => apiMethods.download('/contact/export', params, 'contacts.csv')
};

export const adminAPI = {
  getDashboard: (params) => apiMethods.get('/admin/dashboard', params),
  getUsers: (params) => apiMethods.get('/admin/users', params),
  updateUser: (id, data) => apiMethods.put(`/admin/users/${id}`, data),
  deleteUser: (id) => apiMethods.delete(`/admin/users/${id}`),
  getNewsAnalytics: (params) => apiMethods.get('/admin/analytics/news', params),
  getContactAnalytics: (params) => apiMethods.get('/admin/analytics/contacts', params),
  getSystemInfo: () => apiMethods.get('/admin/system/info'),
  getWebsiteContent: () => apiMethods.get('/admin/website-content'),
  updateWebsiteContent: (data) => apiMethods.put('/admin/website-content', data),
  uploadImage: (formData) => apiMethods.upload('/admin/upload-image', formData)
};

// 工具函数
export const utils = {
  // 处理API响应
  handleResponse: (response) => {
    if (response.data.status === 'success') {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } else {
      return {
        success: false,
        message: response.data.message || '操作失败'
      };
    }
  },

  // 处理API错误
  handleError: (error) => {
    return {
      success: false,
      message: error.response?.data?.message || error.message || '操作失败'
    };
  },

  // 构建查询参数
  buildParams: (params) => {
    const cleanParams = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        cleanParams[key] = params[key];
      }
    });
    return cleanParams;
  }
};

// 导出默认实例和方法
export default {
  ...apiMethods,
  auth: authAPI,
  news: newsAPI,
  contact: contactAPI,
  admin: adminAPI,
  utils
};